import classNames from "classnames";
import React from "react";
import { SimpleFieldProps, useSimpleFieldProps } from "./FormTools";
import { ErrorMessage } from "./Tools";

export function RadioYesNo<T extends string | number | null>(props: {
  fieldProps: SimpleFieldProps<T>;
  children?: React.ReactNode;
}) {
  return <Radio {...props} options={{ Yes: "Yes", No: "No" }} />;
}

export function Radio<T extends string | number | null>(props: {
  fieldProps: SimpleFieldProps<T>;
  children?: React.ReactNode;
  options: { [key: string]: React.ReactNode };
  disabled?: boolean;
}) {
  const field = useSimpleFieldProps(props.fieldProps);
  return (
    <div className="field is-narrow">
      <div className="control">
        {props.children}
        <span style={{ position: "relative", top: "-2px" }}>
          {Object.entries(props.options).map(([key, value], i) => {
            const id = `${field.name}_${key}`;
            return (
              <React.Fragment key={id}>
                <input
                  type="radio"
                  disabled={props.disabled}
                  className={classNames("is-checkradio", {
                    "input-validation-error": field.error,
                  })}
                  id={id}
                  name={field.name}
                  value={key}
                  checked={field.value === key}
                  onFocus={e => field.onFocus(e.target.value as T)}
                  onChange={e => field.onChange(e.target.value as T)}
                  onBlur={e => field.onBlur(e.target.value as T)}
                />
                <label htmlFor={id}>{value}</label>
                {i === Object.keys(props.options).length - 1 && (
                  <ErrorMessage>{field.error}</ErrorMessage>
                )}
              </React.Fragment>
            );
          })}
        </span>
      </div>
    </div>
  );
}

interface LabelAndInputProps {
  label: React.ReactNode;
  inputProps?: React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >;
}

export function VerticalRadio<T extends string | number | undefined>(props: {
  fieldProps: SimpleFieldProps<T>;
  options: Array<[string, React.ReactNode | LabelAndInputProps]>;
  value?: string;
  hasBorderedArea?: true;
}) {
  const field = useSimpleFieldProps(props.fieldProps);
  return (
    <div
      className={classNames({
        "vertical-radio-has-bordered-area": props.hasBorderedArea,
      })}
    >
      {props.options.map(([value, option]) => {
        const { label, inputProps } =
          option !== null && typeof option === "object" && "label" in option
            ? option
            : { label: option, inputProps: {} };
        const id = `${field.name}_${value}`;
        const isSelected = field.value === value;
        return (
          <label key={id} style={{ cursor: "pointer" }}>
            <div
              className={classNames("field vertical-radio-option", {
                "is-selected": isSelected,
              })}
            >
              <span>
                <input
                  {...inputProps}
                  name={field.name}
                  className={classNames("is-checkradio", {
                    "input-validation-error": field.error,
                  })}
                  id={id}
                  type="radio"
                  checked={isSelected}
                  value={value}
                  onFocus={e => field.onFocus(e.target.value as T)}
                  onChange={e => field.onChange(e.target.value as T)}
                  onBlur={e => field.onBlur(e.target.value as T)}
                />
                {/* NOTE: the following label is to get bulma-checkradio to render */}
                <label style={{ paddingLeft: "1em" }} htmlFor={id} />
              </span>
              <span>{label}</span>
            </div>
          </label>
        );
      })}
      <div>
        <ErrorMessage>{field.error}</ErrorMessage>
      </div>
    </div>
  );
}
